import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProfile, adminHideProfile, adminUnhideProfile } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { MapPin, Globe, ShieldAlert, ShieldCheck, ExternalLink, Star, ArrowLeft, Send, Truck, Gauge, Clock, Compass, FileText } from 'lucide-react';
import { getExperienceLevelLabel, getRateTypeLabel, getThomannSearchUrl } from '../lib/utils';
import { toast } from 'sonner';
import { RiderUpload } from '../components/RiderUpload';
import { PageSkeleton, Bone, BoneCard } from '../components/PageSkeleton';

const GEAR_CATEGORIES = [
  { key: 'console', label: 'Console', match: ['console', 'yamaha', 'midas', 'allen', 'behringer', 'digico', 'soundcraft', 'cl5', 'cl3', 'pm5d', 'x32', 'sd'] },
  { key: 'pa', label: 'PA', match: ['pa', 'd&b', 'l-acoustics', 'jbl', 'meyer', 'qsc', 'martin', 'electrovoice', 'ev ', 'audiotechnik', 'line array'] },
  { key: 'monitoring', label: 'Monitoring', match: ['monitor', 'shure psm', 'iem', 'sennheiser', 'in-ear', 'wedge'] },
  { key: 'mics', label: 'Microphones', match: ['mic', 'shure sm', 'neumann', 'akg', 'rode', 'sennheiser e', 'sm57', 'sm58', 'beta'] },
  { key: 'lighting', label: 'Lighting', match: ['light', 'dmx', 'moving head', 'par', 'spot', 'wash', 'grandma', 'chamsys'] },
];

function categorizeGear(gearList) {
  if (!gearList?.length) return [];
  const categorized = [];
  const used = new Set();

  for (const cat of GEAR_CATEGORIES) {
    const items = gearList.filter((g, i) => {
      if (used.has(i)) return false;
      const lower = g.toLowerCase();
      return cat.match.some(m => lower.includes(m));
    });
    if (items.length > 0) {
      items.forEach(item => used.add(gearList.indexOf(item)));
      categorized.push({ label: cat.label, items });
    }
  }

  const other = gearList.filter((_, i) => !used.has(i));
  if (other.length > 0) categorized.push({ label: 'Other', items: other });

  return categorized;
}

export default function TechDetailPage({ profileId: propId, embedded = false }) {
  const params = useParams();
  const id = propId || params.id;
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tech, setTech] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!embedded && id === 'me') { navigate('/tech/me', { replace: true }); return; }
    loadTech();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate, embedded]);

  const loadTech = async () => {
    try {
      const response = await getProfile(id);
      if (response.data.type !== 'TECH') {
        if (!embedded) navigate('/tech', { replace: true });
        return;
      }
      setTech(response.data);
    } catch (error) {
      if (!embedded) {
        toast.error('Tech profile not found');
        navigate('/tech', { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={embedded ? 'px-5 py-4' : 'page-container-md'}>
        <PageSkeleton label="Loading crew member">
          {!embedded && <Bone w={64} h={14} className="mb-5" />}
          <BoneCard className="p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <Bone w="68%" h={34} />
                <Bone w={64} h={14} className="mt-3" />
                <Bone w="58%" h={14} className="mt-4" />
              </div>
              <Bone w={64} h={24} tone="amber" />
            </div>
          </BoneCard>
          <div className="flex justify-center my-5"><Bone w={150} h={48} r="pill" tone="amber" /></div>
          <BoneCard className="p-5">
            <Bone w="46%" h={20} />
            <div className="grid grid-cols-2 gap-2 mt-4"><Bone h={68} r="md" /><Bone h={68} r="md" /></div>
            <Bone w={80} h={12} className="mt-5" />
            <div className="flex gap-2 mt-2"><Bone w={90} h={24} /></div>
            <Bone w={110} h={12} className="mt-4" />
            <div className="flex gap-2 mt-2"><Bone w={48} h={24} /><Bone w={40} h={24} /><Bone w={48} h={24} /></div>
          </BoneCard>
        </PageSkeleton>
      </div>
    );
  }

  if (!tech) return null;

  const rate = tech.baseRate;
  const rateLabel = getRateTypeLabel(tech.rateType);
  const gearGroups = categorizeGear(tech.gearCapabilities);
  // Mirror MusicianDetailPage: fall back to the onboarding experience field so a
  // tech who never opened the editor (e.g. a touring_pro) still shows their level.
  const declaredExperience = tech.techExperienceLevel || tech.experienceLevel;

  return (
    <div className={embedded ? 'px-5 py-4' : 'page-container-md'} data-testid="tech-detail-page">

      {/* Back — hidden when embedded in 3-pane directory */}
      {!embedded && (
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 mb-5 transition-colors"
          style={{ color: 'var(--s8-text-muted)', fontFamily: 'var(--s8-font-mono)', fontSize: '12px', letterSpacing: '1px' }}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>BACK</span>
        </button>
      )}

      {/* ── 1. Header Card: Name + Role + Location + Rate ── */}
      <div
        className="mb-5 p-4 s8-glow-border"
        style={{
          background: 'linear-gradient(180deg, rgba(240, 235, 224, 0.02) 0%, var(--s8-bg-surface) 100%)',
          border: '1px solid var(--s8-border-card)',
          borderRadius: 'var(--s8-radius-lg)',
          boxShadow: 'var(--card-shadow)',
        }}
        data-testid="tech-header"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1
              style={{ fontFamily: 'var(--s8-font-display)', fontWeight: 600, fontSynthesis: 'none', fontSize: 'clamp(1.875rem, 1.875rem + 1vw, 2.25rem)', lineHeight: 1.1, letterSpacing: '-0.02em', textWrap: 'balance', color: 'var(--s8-text-main)' }}
              data-testid="tech-name"
            >
              {tech.displayName}
            </h1>
            {tech.primaryRole && (
              <div className="mt-1.5" style={{ fontFamily: 'var(--s8-font-body)', fontWeight: 600, fontSize: '14px', color: 'var(--s8-text-body)' }} data-testid="tech-role-header">
                {tech.primaryRole}
              </div>
            )}
          </div>
          {rate && (
            <div className="shrink-0 text-right">
              <span style={{ fontFamily: 'var(--s8-font-body)', fontWeight: 600, fontSize: '22px', color: 'var(--s8-amber)' }} data-testid="tech-rate">
                €{rate}
              </span>
              <div style={{ fontFamily: 'var(--s8-font-body)', fontSize: '12px', color: 'var(--s8-text-muted)' }}>{rateLabel}</div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-2" style={{ fontFamily: 'var(--s8-font-body)', fontSize: '14px', color: 'var(--s8-text-muted)' }} data-testid="tech-location">
          <MapPin className="h-3.5 w-3.5 shrink-0" />
          <span>
            {tech.city}{tech.country ? `, ${tech.country}` : ''}
            {tech.travelRadiusKm ? ` · ${tech.travelRadiusKm}km radius` : ''}
          </span>
        </div>
      </div>

      {/* ── Primary Action ── */}
      <button
        className="btn-primary w-full mb-5"
        data-testid="invite-to-gig-btn"
        onClick={() => toast.info('Invite flow coming soon')}
      >
        <Send className="h-4 w-4" />
        INVITE TO GIG
      </button>

      {/* ── 2. Capabilities: Role + Experience ── */}
      <div
        className="mb-3 p-4"
        style={{
          background: 'linear-gradient(180deg, rgba(240, 235, 224, 0.02) 0%, var(--s8-bg-surface) 100%)',
          border: '1px solid var(--s8-border-card)',
          borderRadius: 'var(--s8-radius-lg)',
          boxShadow: 'var(--card-shadow)',
        }}
        data-testid="capabilities-card"
      >
        <h2
          className="mb-3"
          style={{ fontFamily: 'var(--s8-font-mono)', fontWeight: 500, fontSize: '18px', color: 'var(--s8-text-main)', letterSpacing: '1.5px', lineHeight: 1.15 }}
        >
          CAPABILITIES
        </h2>
        <div className="grid grid-cols-2 gap-px mb-3" style={{ background: 'var(--border-subtle)', borderRadius: 'var(--s8-radius-md)', overflow: 'hidden' }}>
          <div className="p-3" style={{ background: 'var(--s8-bg-elevated)' }}>
            <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
              Role
            </div>
            <div style={{ fontFamily: 'var(--s8-font-body)', fontWeight: 600, fontSize: '16px', color: 'var(--s8-text-main)' }}>
              {tech.primaryRole || '—'}
            </div>
          </div>
          <div className="p-3" style={{ background: 'var(--s8-bg-elevated)' }}>
            <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
              Experience
            </div>
            <div style={{ fontFamily: 'var(--s8-font-body)', fontWeight: 600, fontSize: '16px', color: 'var(--s8-text-main)' }}>
              {declaredExperience ? getExperienceLevelLabel(declaredExperience, 'tech') : '—'}
            </div>
          </div>
        </div>
        {/* Specialties as tags */}
        {tech.specialties?.length > 0 && (
          <div>
            <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>
              Specialties
            </div>
            <div className="flex flex-wrap gap-1.5">
              {tech.specialties.map((s, i) => (
                <span key={`skill-${s}-${i}`} className="browse-tag">{s}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── 3. Operational Record: Gigs + Rating + Rebook ── */}
      <div
        className="mb-3 p-4"
        style={{
          background: 'linear-gradient(180deg, rgba(240, 235, 224, 0.02) 0%, var(--s8-bg-surface) 100%)',
          border: '1px solid var(--s8-border-card)',
          borderRadius: 'var(--s8-radius-lg)',
          boxShadow: 'var(--card-shadow)',
        }}
        data-testid="operational-record-card"
      >
        <h2
          className="mb-3"
          style={{ fontFamily: 'var(--s8-font-mono)', fontWeight: 500, fontSize: '18px', color: 'var(--s8-text-main)', letterSpacing: '1.5px', lineHeight: 1.15 }}
        >
          OPERATIONAL RECORD
        </h2>
        <div className="grid grid-cols-3 gap-px" style={{ background: 'var(--border-subtle)', borderRadius: 'var(--s8-radius-md)', overflow: 'hidden' }}>
          <div className="p-3" style={{ background: 'var(--s8-bg-elevated)' }}>
            <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
              STAGE8 Gigs
            </div>
            <div style={{ fontFamily: 'var(--s8-font-body)', fontWeight: 600, fontSize: '18px', color: 'var(--s8-text-main)' }}>
              {tech.stage8GigCount || 0}
            </div>
          </div>
          <div className="p-3" style={{ background: 'var(--s8-bg-elevated)' }}>
            <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
              Rating
            </div>
            <div className="flex items-center gap-1.5">
              <Star className="h-3.5 w-3.5 fill-[var(--s8-amber)] text-[var(--s8-amber)]" />
              <span style={{ fontFamily: 'var(--s8-font-body)', fontWeight: 600, fontSize: '18px', color: 'var(--s8-text-main)' }} data-testid="tech-rating">
                {tech.averageRating || '—'}
              </span>
            </div>
          </div>
          <div className="p-3" style={{ background: 'var(--s8-bg-elevated)' }}>
            <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
              Rebooked
            </div>
            <div style={{ fontFamily: 'var(--s8-font-body)', fontWeight: 600, fontSize: '18px', color: 'var(--s8-amber)' }} data-testid="tech-rebook">
              {tech.rebookRate ? `${tech.rebookRate}%` : '—'}
            </div>
          </div>
        </div>
      </div>

      {/* ── 4. Equipment Ownership ── */}
      {tech.hasOwnEquipment && (
        <div
          className="mb-3 p-4"
          style={{
            background: 'linear-gradient(180deg, rgba(240, 235, 224, 0.02) 0%, var(--s8-bg-surface) 100%)',
            border: '1px solid var(--s8-border-card)',
            borderRadius: 'var(--s8-radius-lg)',
            boxShadow: 'var(--card-shadow)',
          }}
          data-testid="equipment-card"
        >
          <h2
            className="mb-3"
            style={{ fontFamily: 'var(--s8-font-mono)', fontWeight: 500, fontSize: '18px', color: 'var(--s8-text-main)', letterSpacing: '1.5px', lineHeight: 1.15 }}
          >
            EQUIPMENT
          </h2>
          <div className="flex items-center gap-2 mb-3" style={{ fontFamily: 'var(--s8-font-body)', fontWeight: 600, fontSize: '14px', color: 'var(--s8-amber)' }}>
            <Gauge className="h-4 w-4" />
            <span>Brings own equipment</span>
          </div>
          {/* Grouped gear list */}
          {gearGroups.length > 0 && (
            <div className="space-y-3">
              {gearGroups.map((group) => (
                <div key={group.label}>
                  <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
                    {group.label}
                  </div>
                  {group.items.map((gear, i) => (
                    <a
                      key={`gear-${gear}-${i}`}
                      href={getThomannSearchUrl(gear)}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="flex items-center gap-1.5 transition-colors hover:text-[var(--s8-amber)]"
                      style={{ fontFamily: 'var(--s8-font-body)', fontSize: '14px', color: 'var(--s8-text-body)', marginBottom: '2px' }}
                    >
                      <span>{gear}</span>
                      <ExternalLink className="h-3 w-3 shrink-0" style={{ color: 'var(--s8-text-muted)' }} />
                    </a>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── 5. Operational Fit ── */}
      <div
        className="mb-3 p-4"
        style={{
          background: 'linear-gradient(180deg, rgba(240, 235, 224, 0.02) 0%, var(--s8-bg-surface) 100%)',
          border: '1px solid var(--s8-border-card)',
          borderRadius: 'var(--s8-radius-lg)',
          boxShadow: 'var(--card-shadow)',
        }}
        data-testid="operational-fit-card"
      >
        <h2
          className="mb-3"
          style={{ fontFamily: 'var(--s8-font-mono)', fontWeight: 500, fontSize: '18px', color: 'var(--s8-text-main)', letterSpacing: '1.5px', lineHeight: 1.15 }}
        >
          OPERATIONAL FIT
        </h2>
        <div className="space-y-2.5">
          {[
            { icon: Compass, label: 'Travel radius', value: tech.travelRadiusKm ? `${tech.travelRadiusKm} km` : '—' },
            { icon: Truck, label: 'Transport', value: tech.hasOwnEquipment ? 'Own vehicle' : 'Needs transport' },
            { icon: Gauge, label: 'Load-in capability', value: tech.hasOwnEquipment ? 'Heavy rigs OK' : 'Standard' },
            { icon: Clock, label: 'Short notice', value: 'Available' },
          ].map((row) => (
            <div key={row.label} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <row.icon className="h-3.5 w-3.5 shrink-0" style={{ color: 'var(--s8-text-muted)' }} />
                <span style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '12px', color: 'var(--s8-text-muted)', letterSpacing: '0.5px' }}>
                  {row.label}
                </span>
              </div>
              <span style={{ fontFamily: 'var(--s8-font-body)', fontWeight: 600, fontSize: '14px', color: 'var(--s8-text-main)' }}>
                {row.value}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ── 6. Details: Roles → Event Types → Languages ── */}
      {(tech.roles?.length > 0 || tech.eventTypes?.length > 0 || tech.languages?.length > 0) && (
        <div
          className="mb-3 p-4"
          style={{
            background: 'linear-gradient(180deg, rgba(240, 235, 224, 0.02) 0%, var(--s8-bg-surface) 100%)',
            border: '1px solid var(--s8-border-card)',
            borderRadius: 'var(--s8-radius-lg)',
            boxShadow: 'var(--card-shadow)',
          }}
          data-testid="details-card"
        >
          <h2
            className="mb-4"
            style={{ fontFamily: 'var(--s8-font-mono)', fontWeight: 500, fontSize: '18px', color: 'var(--s8-text-main)', letterSpacing: '1.5px', lineHeight: 1.15 }}
          >
            DETAILS
          </h2>
          <div className="space-y-5">
            {/* Roles */}
            {tech.roles?.length > 0 && (
              <div>
                <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                  Roles
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {tech.roles.map((role, i) => (
                    <span key={`role-${role}-${i}`} className="browse-tag">{role}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Event Types */}
            {tech.eventTypes?.length > 0 && (
              <div>
                <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                  Event Types
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {tech.eventTypes.map((t, i) => (
                    <span key={`tool-${t}-${i}`} className="browse-tag">{t}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Languages */}
            {tech.languages?.length > 0 && (
              <div>
                <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                  Languages
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {tech.languages.map((l, i) => (
                    <span key={`lang-${l}-${i}`} className="browse-tag flex items-center gap-1">
                      <Globe className="h-3 w-3" style={{ color: 'var(--s8-text-muted)' }} />
                      {l}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── 7. About — bottom ── */}
      {tech.shortBio && (
        <div
          className="mb-3 p-4"
          style={{
            background: 'linear-gradient(180deg, rgba(240, 235, 224, 0.02) 0%, var(--s8-bg-surface) 100%)',
            border: '1px solid var(--s8-border-card)',
            borderRadius: 'var(--s8-radius-lg)',
            boxShadow: 'var(--card-shadow)',
          }}
          data-testid="about-card"
        >
          <h2
            className="mb-3"
            style={{ fontFamily: 'var(--s8-font-mono)', fontWeight: 500, fontSize: '18px', color: 'var(--s8-text-main)', letterSpacing: '1.5px', lineHeight: 1.15 }}
          >
            ABOUT
          </h2>
          <p style={{ fontFamily: 'var(--s8-font-body)', fontSize: '14px', lineHeight: 1.55, color: 'var(--s8-text-body)' }}>
            {tech.shortBio}
          </p>
        </div>
      )}

      {/* Portfolio */}
      {tech.portfolioUrl && (
        <div className="mb-3">
          <a
            href={tech.portfolioUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 transition-colors hover:text-[var(--s8-text-main)]"
            style={{ fontFamily: 'var(--s8-font-condensed)', fontWeight: 600, fontSize: '14px', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--s8-amber)' }}
            data-testid="portfolio-link"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            VIEW PORTFOLIO
          </a>
        </div>
      )}

      {/* ── Riders ── */}
      <div className="mb-3">
        <div
          className="p-4"
          style={{
            background: 'linear-gradient(180deg, rgba(240, 235, 224, 0.02) 0%, var(--s8-bg-surface) 100%)',
            border: '1px solid var(--s8-border-card)',
            borderRadius: 'var(--s8-radius-lg)',
            boxShadow: 'var(--card-shadow)',
          }}
          data-testid="riders-card"
        >
          <h2
            className="mb-3"
            style={{ fontFamily: 'var(--s8-font-mono)', fontWeight: 500, fontSize: '18px', color: 'var(--s8-text-main)', letterSpacing: '1.5px', lineHeight: 1.15 }}
          >
            RIDERS
          </h2>
          <RiderUpload profileId={id} editable={false} />
        </div>
      </div>

      {/* Admin Controls */}
      {user?.isAdmin && (
        <div className="mt-6 pt-4 flex items-center justify-between" style={{ borderTop: '1px solid var(--border-subtle)' }} data-testid="admin-controls">
          <div className="flex items-center gap-2" style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '11px', color: 'var(--s8-red-tag-text)' }}>
            <ShieldAlert className="h-3 w-3" />
            <span>ADMIN</span>
          </div>
          {tech.hidden ? (
            <Button variant="outline" size="sm" className="border-[var(--s8-amber)]/30 text-[var(--s8-amber)] hover:bg-[var(--s8-amber)]/15 text-xs"
              onClick={async () => { try { await adminUnhideProfile(tech.id); toast.success('Restored'); loadTech(); } catch { toast.error('Failed'); } }}
              data-testid="admin-unhide-profile-btn">
              <ShieldCheck className="h-3 w-3 mr-1" /> Restore
            </Button>
          ) : (
            <Button variant="outline" size="sm" className="border-red-500/25 text-red-400 hover:bg-red-500/10 text-xs"
              onClick={async () => { try { await adminHideProfile(tech.id); toast.success('Hidden'); navigate('/tech'); } catch { toast.error('Failed'); } }}
              data-testid="admin-hide-profile-btn">
              <ShieldAlert className="h-3 w-3 mr-1" /> Hide
            </Button>
          )}
        </div>
      )}

    </div>
  );
}
